import { useState, useMemo } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useMcpConnections } from '../../hooks/useMcpConnections';
import { useAgentMcpConnections, useAddAgentMcpConnection } from '../../hooks/useAgents';
import { McpConnection } from '../../types';

interface AgentMcpConnectionSelectorProps {
  agentId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function AgentMcpConnectionSelector({ agentId, isOpen, onClose }: AgentMcpConnectionSelectorProps) {
  const [search, setSearch] = useState('');
  const [selectedConnection, setSelectedConnection] = useState<McpConnection | null>(null);
  const { data, isLoading, error } = useMcpConnections(1, 100, { search: search || undefined });
  const { data: linkedConnections } = useAgentMcpConnections(agentId);
  const addConnection = useAddAgentMcpConnection();

  const allConnections = data?.data || [];
  const linkedIds = useMemo(() => new Set(linkedConnections?.map(lc => lc.mcpConnectionId) || []), [linkedConnections]);

  // Filter out already-linked connections
  const availableConnections = useMemo(
    () => allConnections.filter(conn => !linkedIds.has(conn.id)),
    [allConnections, linkedIds]
  );

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !addConnection.isPending) {
      handleClose();
    }
  };

  const handleClose = () => {
    setSearch('');
    setSelectedConnection(null);
    onClose();
  };

  const handleSelectConnection = (connection: McpConnection) => {
    setSelectedConnection(connection);
  };

  const handleAdd = async () => {
    if (!selectedConnection) return;

    try {
      await addConnection.mutateAsync({ agentId, mcpConnectionId: selectedConnection.id });
      handleClose();
    } catch (error) {
      console.error('Failed to add MCP connection:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="mcp-selector-title"
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[80vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 id="mcp-selector-title" className="text-lg font-medium text-gray-900 dark:text-white">
            Add MCP Connection
          </h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={addConnection.isPending}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close modal"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-auto px-6 py-4">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Search MCP connections..."
              />
            </div>
          </div>

          {/* Connection List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm text-gray-500 dark:text-gray-400">Loading connections...</div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-sm text-red-600 dark:text-red-400">
                Error loading connections. Please try again.
              </p>
            </div>
          ) : availableConnections.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {search
                  ? `No available connections found matching "${search}"`
                  : allConnections.length === 0
                    ? 'No MCP connections available'
                    : 'All MCP connections are already linked to this agent'}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {availableConnections.map((connection) => (
                <button
                  key={connection.id}
                  type="button"
                  onClick={() => handleSelectConnection(connection)}
                  className={`w-full text-left p-4 rounded-lg border-2 transition-colors ${
                    selectedConnection?.id === connection.id
                      ? 'border-indigo-600 dark:border-indigo-400 bg-indigo-50 dark:bg-indigo-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900 dark:text-white">
                      {connection.name}
                    </span>
                    {connection.isActive ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400 truncate">
                    {connection.url}
                  </div>
                  {connection.description && (
                    <div className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                      {connection.description}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 rounded-b-lg">
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={addConnection.isPending}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!selectedConnection || addConnection.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 dark:bg-indigo-700 border border-transparent rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addConnection.isPending ? 'Adding...' : 'Add Connection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
